import styled from 'styled-components';
import {
  AddFilterCommands,
  ChangeConditionCommands,
  EditFilterCommands,
} from './viewpointBuilderFiltersCommands';
import {
  GraphId,
  TraversalBuilderFiltersPerNode,
  TraversalBuilderViewState,
  ViewpointBuilderFiltersDefinitions,
} from '../types';
import { ViewpointBuilderFilterComponent } from './ViewpointBuilderFilterComponent';
import { GhostButton } from '@ardoq/button';
import { Icon, IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { Select } from '@ardoq/select';
import { header4 } from '@ardoq/typography';
import { BooleanOperator } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

type FiltersProps = {
  commands: AddFilterCommands & ChangeConditionCommands & EditFilterCommands;
  filtersWithCondition: TraversalBuilderFiltersPerNode;
  selectedGraphNodeId: string;
  availableFilterGroupedOptions: ViewpointBuilderFiltersDefinitions['componentFilterTypeOptions'];
  asyncLoaders: TraversalBuilderViewState['asyncLoaders'];
  locale: Locale;
  i18nLocale: I18nLocale;
};

export const ViewpointBuilderFilters = ({
  commands,
  filtersWithCondition,
  selectedGraphNodeId,
  availableFilterGroupedOptions,
  asyncLoaders,
  locale,
  i18nLocale,
}: FiltersProps) => {
  return (
    <FlexBox
      flexDirection="column"
      align="stretch"
      gap="medium"
      padding="medium"
    >
      <FilterCondition
        commands={commands}
        condition={filtersWithCondition.condition}
        selectedGraphNodeId={selectedGraphNodeId}
        filtersCount={filtersWithCondition.filters.length}
        preservedRulesCount={filtersWithCondition.preservedRules.length}
        i18nLocale={i18nLocale}
      />
      {filtersWithCondition.filters.map(filter => (
        <ViewpointBuilderFilterComponent
          availableFilterGroupedOptions={availableFilterGroupedOptions}
          key={filter.nonPersistentId}
          commands={commands}
          filter={filter}
          asyncLoaders={asyncLoaders}
          locale={locale}
          i18nLocale={i18nLocale}
        />
      ))}
      <GhostButton
        isFlexible
        onClick={() => commands.addDefaultFilter(selectedGraphNodeId)}
      >
        <Icon iconName={IconName.ADD_CIRCLE} />
        {t(
          i18nLocale,
          'viewpointBuilder:addFilters.addAnother',
          'Add another filter'
        )}
      </GhostButton>
    </FlexBox>
  );
};

type FilterConditionProps = {
  condition: BooleanOperator;
  filtersCount: number;
  /**
   * Rules on this node the panel cannot show — a component exclusion, today.
   *
   * They are saved under this condition, so `any` would widen the filter to include `_id != X`,
   * which nearly everything satisfies: the rules above would stop narrowing anything and nothing
   * on screen would say why. `canExcludeComponent` refuses the mirror of this from the other side,
   * declining to add an exclusion to a filter already combining rules with `OR`.
   */
  preservedRulesCount: number;
  commands: ChangeConditionCommands;
  selectedGraphNodeId: GraphId;
  i18nLocale: I18nLocale;
};

const FilterCondition = ({
  condition,
  filtersCount,
  preservedRulesCount,
  commands,
  selectedGraphNodeId,
  i18nLocale,
}: FilterConditionProps) => {
  if (filtersCount < 2) {
    return null;
  }

  return (
    <Header4Text align="baseline" flex={1} justify="start" flexWrap>
      <Span>
        {t(i18nLocale, 'viewpointBuilder:addFilters.mustMatch', 'Must match')}
      </Span>
      <FixedWidthWrapper>
        <Select
          dataTestId="viewpoint-builder-select-condition"
          value={condition}
          options={[
            {
              label: 'all',
              value: BooleanOperator.AND,
            },
            {
              label: 'any',
              value: BooleanOperator.OR,
              isDisabled: preservedRulesCount > 0,
              tooltipText:
                preservedRulesCount > 0
                  ? t(
                      i18nLocale,
                      'viewpointBuilder:addFilters.anyBlockedByExclusion',
                      'Not available while this dataset excludes a component. Restore it from the toolbar first.'
                    )
                  : undefined,
            },
          ]}
          aria-label={t(
            i18nLocale,
            'viewpointBuilder:addFilters.conditionOperatorAriaLabel',
            'Condition operator'
          )}
          onChange={selectOption => {
            if (!selectOption) return;

            commands.changeFilterCondition({
              condition: selectOption.value,
              graphNodeId: selectedGraphNodeId,
            });
          }}
          i18nLocale={i18nLocale}
        />
      </FixedWidthWrapper>
      <Span>
        {t(
          i18nLocale,
          'viewpointBuilder:addFilters.followingRules',
          'of the following rules'
        )}
      </Span>
    </Header4Text>
  );
};

const Header4Text = styled(FlexBox).attrs({ gap: 'medium' })`
  ${header4}
`;

const Span = styled.span`
  display: inline-block;
  white-space: normal;
`;

const FixedWidthWrapper = styled.div`
  width: 80px;
`;
